"use client";

import { useEffect, useRef, useState } from "react";
import { HOMEPAGE_RANGE } from "../content/expression-studies";
import styles from "./home-page.module.css";
import { VerbaloreMark } from "./verbalore-mark";

type HomePageProps = {
  onBegin: () => void;
  onBrowse: () => void;
  onPractice: (view?: "words" | "expression") => void;
};

const JOURNEY = [
  {
    number: "01",
    label: "Read",
    title: "Let the sentence keep its rhythm.",
    copy: "No definitions crowd the margin. No interface asks for attention before the language earns it.",
  },
  {
    number: "02",
    label: "Glance",
    title: "Understand enough to continue.",
    copy: "A deliberate touch opens a maintained definition. Curated words add the meaning and craft written for this sentence.",
  },
  {
    number: "03",
    label: "Explore",
    title: "Follow curiosity as far as it goes.",
    copy: "Senses, shade, contrast, voice, word family, and usage appear only when you ask for greater depth.",
  },
  {
    number: "04",
    label: "Return",
    title: "Come back to the exact thought.",
    copy: "The page does not jump, forget, or punish the detour. Your place and the reason the word mattered remain intact.",
  },
];

export function HomePage({ onBegin, onBrowse, onPractice }: HomePageProps) {
  const rootRef = useRef<HTMLElement>(null);
  const stageRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [activeStage, setActiveStage] = useState(0);
  const [activeRangeId, setActiveRangeId] = useState<string>("tactful");
  const activeRange = HOMEPAGE_RANGE.stances.find((stance) => stance.id === activeRangeId)
    || HOMEPAGE_RANGE.stances[0];

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const elements = Array.from(root.querySelectorAll<HTMLElement>("[data-home-reveal]"));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add(styles.revealed);
        });
      },
      { rootMargin: "0px 0px -12%", threshold: 0.16 },
    );
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActiveStage(Number((visible.target as HTMLElement).dataset.stage || 0));
      },
      { rootMargin: "-31% 0px -42%", threshold: [0.15, 0.45, 0.75] },
    );
    stageRefs.current.forEach((element) => element && observer.observe(element));
    return () => observer.disconnect();
  }, []);

  return (
    <main className={styles.home} ref={rootRef}>
      <header className={styles.nav}>
        <a className={styles.brand} href="#home" aria-label="Verbalore home">
          <VerbaloreMark className={styles.brandMark} />
          <strong>Verbalore</strong>
        </a>
        <nav aria-label="Homepage navigation">
          <a href="#method">The method</a>
          <a href="#range">Command</a>
          <button type="button" onClick={onBegin}>Begin reading</button>
        </nav>
      </header>

      <section className={styles.hero} id="home">
        <div className={styles.heroCopy} data-home-reveal>
          <span className={styles.kicker}>The weight of words</span>
          <h1>Language should not merely sound impressive.</h1>
          <p className={styles.heroTurn}>It should do what you mean.</p>
          <p className={styles.heroIntro}>
            Read writing with force. Pause at any word for a maintained meaning. Curated words reveal their exact work; your sentence and place remain intact.
          </p>
          <div className={styles.heroActions}>
            <button type="button" onClick={onBrowse}>Choose a story <span aria-hidden="true">→</span></button>
            <button className={styles.continueReading} type="button" onClick={onBegin}>Continue current reading</button>
          </div>
        </div>

        <div className={styles.heroObject} aria-label="A word opened from its literary context" data-home-reveal>
          <div className={styles.paperGlow} />
          <article className={styles.passageCard}>
            <span>A Verbalore example</span>
            <p>
              “He possessed the rare art of making an accusation sound like a confidence, and an <mark>equivocation</mark> like an act of mercy.”
            </p>
            <small>Tap any word. Keep the sentence.</small>
          </article>
          <aside className={styles.wordCard}>
            <div><span>Glance</span><i>01</i></div>
            <h2>equivocation</h2>
            <p className={styles.phonetic}>noun · /ɪˌkwɪvəˈkeɪʃən/</p>
            <span className={styles.meaningLabel}>Meaning here</span>
            <p>A deliberate ambiguity used to avoid committing to the truth.</p>
            <button type="button" onClick={onBegin}>Enter the reading <span>↗</span></button>
          </aside>
          <span className={`${styles.orbitWord} ${styles.orbitOne}`}>shade</span>
          <span className={`${styles.orbitWord} ${styles.orbitTwo}`}>voice</span>
          <span className={`${styles.orbitWord} ${styles.orbitThree}`}>context</span>
        </div>
      </section>

      <section className={styles.manifesto} aria-label="Verbalore principle">
        <div data-home-reveal>
          <p>Read without interruption.</p>
          <p>Explore without limitation.</p>
          <p>Return without losing anything.</p>
        </div>
        <span data-home-reveal>The central problem is not access to knowledge. It is protecting attention while preserving curiosity.</span>
      </section>

      <section className={styles.method} id="method">
        <header data-home-reveal>
          <span className={styles.kicker}>One reading. Two speeds.</span>
          <h2>Stay with the story.<br />Or open the universe.</h2>
          <p>You decide the depth. Verbalore never decides that your curiosity is an interruption.</p>
        </header>

        <div className={styles.journey}>
          <div className={styles.stickyDemo} aria-live="polite">
            <div className={styles.demoTop}>
              <span>{JOURNEY[activeStage].number}</span>
              <small>{JOURNEY[activeStage].label}</small>
              <div className={styles.stageDots} aria-hidden="true">
                {JOURNEY.map((item, index) => <i key={item.number} className={index === activeStage ? styles.active : ""} />)}
              </div>
            </div>
            <div className={styles.demoSentence}>
              <p>“There are refusals which close a door, and others which merely teach a visitor to knock with greater <button type="button" onClick={onBegin}>discernment</button>.”</p>
            </div>
            <div className={`${styles.demoLayer} ${styles[`stage${activeStage}`]}`}>
              <span>{activeStage === 0 ? "Nothing demands attention" : activeStage === 1 ? "Meaning here" : activeStage === 2 ? "The word universe" : "Your place is waiting"}</span>
              <strong>{activeStage === 0 ? "The sentence remains whole." : activeStage === 1 ? "Judgment that notices subtle distinctions." : activeStage === 2 ? "discern · discriminate · perceive · distinguish" : "Return to discernment →"}</strong>
              {activeStage > 0 && activeStage < 3 ? <small>{activeStage === 1 ? "Enough to keep reading." : "Shade · contrast · intonation · family · use"}</small> : null}
            </div>
          </div>

          <div className={styles.journeySteps}>
            {JOURNEY.map((item, index) => (
              <div
                key={item.number}
                ref={(element) => { stageRefs.current[index] = element; }}
                data-stage={index}
                className={index === activeStage ? styles.currentStep : ""}
              >
                <span>{item.number} · {item.label}</span>
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.command} id="words">
        <div className={styles.commandIntro} data-home-reveal>
          <span className={styles.kicker}>Vocabulary with consequence</span>
          <h2>A library should not be a sack of saved words.</h2>
          <p>Every word keeps the sentence in which it mattered, every later encounter, and honest evidence of what you can actually command.</p>
          <button type="button" onClick={() => onPractice("words")}>See words to command <span>→</span></button>
        </div>
        <div className={styles.learningThread} data-home-reveal>
          <header><span>One word, becoming yours</span><strong>discernment</strong></header>
          <ol>
            <li className={styles.complete}><i>✓</i><div><span>Encountered</span><p>Met in a refusal that preserved dignity.</p><small>Original sentence retained</small></div></li>
            <li className={styles.complete}><i>✓</i><div><span>Active</span><p>Recalled, distinguished, and used in a sentence of your own.</p><small>Evidence, not a streak</small></div></li>
            <li><i>3</i><div><span>Owned</span><p>Recall it later. Notice it entering language beyond the exercise.</p><small>Mastery is never awarded cheaply</small></div></li>
          </ol>
        </div>
      </section>

      <section className={styles.range} id="range">
        <header data-home-reveal>
          <span className={styles.kicker}>Verbal Range</span>
          <h2>You already know what you mean.<br /><em>Command is choosing how it lands.</em></h2>
          <p>One intention can be direct, tactful, warm, restrained, or punchy without becoming a different intention.</p>
        </header>

        <div className={styles.rangeFrame} data-home-reveal>
          <aside className={styles.rangeIntention}>
            <span>Meaning held constant</span>
            <p>{HOMEPAGE_RANGE.intention}</p>
            <small>{HOMEPAGE_RANGE.invariant}</small>
          </aside>

          <div className={styles.rangeExperience}>
            <div className={styles.rangeChoices} aria-label="Choose how the intention lands">
              {HOMEPAGE_RANGE.stances.map((stance) => (
                <button
                  key={stance.id}
                  type="button"
                  className={stance.id === activeRange.id ? styles.activeRange : ""}
                  aria-pressed={stance.id === activeRange.id}
                  onClick={() => setActiveRangeId(stance.id)}
                >
                  {stance.label}
                </button>
              ))}
            </div>

            <article className={styles.rangeSentence} key={activeRange.id} aria-live="polite">
              <span>{activeRange.label}</span>
              <blockquote>“{activeRange.line}”</blockquote>
              <p>{activeRange.landing}</p>
            </article>

            <div className={styles.rangePayoff}>
              <p>Same meaning. You choose how it lands.</p>
              <button type="button" onClick={() => onPractice("expression")}>Enter Expression Studio <span aria-hidden="true">→</span></button>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.finalCta}>
        <div data-home-reveal>
          <VerbaloreMark className={styles.ctaMark} />
          <p>Words are not ornaments.</p>
          <h2>Give yours weight.</h2>
          <button type="button" onClick={onBegin}>Begin with a story <span>→</span></button>
          <small>Read freely. Sign in only when you want your learning preserved.</small>
        </div>
      </section>

      <footer className={styles.footer}>
        <div><strong>Verbalore</strong><span>The weight of words</span></div>
        <p>Built for attention, curiosity, and language that carries its own weight.</p>
        <button type="button" onClick={onBegin}>Enter reading room ↑</button>
      </footer>
    </main>
  );
}
